import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useBatchTaskImageUrls, useTaskImages } from "@dashboard/hooks/useTaskImageQuery";
import type { TaskImage } from "@dashboard/api/task";

function TaskImageThumb({
    image,
    onDelete,
    url,
}: {
    image: TaskImage;
    onDelete: () => void;
    url?: string;
}) {
    const { t } = useTranslation();

    return (
        <div className="relative group w-14 h-14 shrink-0">
            <div className="w-full h-full rounded border border-compendia-border overflow-hidden">
                {url ? (
                    <img
                        src={url}
                        alt={image.fileName}
                        loading="lazy"
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full bg-compendia-greige-20 animate-pulse" />
                )}
            </div>
            <button
                type="button"
                className="absolute -top-1 -right-1 w-5 h-5 bg-chameleon-pink/20 border border-chameleon-pink rounded text-chameleon-pink hover:bg-chameleon-pink/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={onDelete}
                aria-label={t("common.deleteImage")}
            >
                <FontAwesomeIcon icon={faTrashCan} size="xs" />
            </button>
        </div>
    );
}

export function ListImagesSection({
    taskId,
    deletedImages,
    onDeleteImage,
}: {
    taskId: number;
    deletedImages: { imageId: number; taskId: number }[];
    onDeleteImage: (imageId: number) => void;
}) {
    const { data } = useTaskImages(taskId);

    const deletedImageIds = deletedImages
        .filter((item) => item.taskId === taskId)
        .map((item) => item.imageId);
    const images = (data?.data ?? []).filter((image) => !deletedImageIds.includes(image.id));
    const imageIds = images.map((img) => img.id);

    const { data: batchUrls } = useBatchTaskImageUrls(imageIds);

    const urlMap = useMemo(
        () => new Map(batchUrls?.map((item) => [item.id, item.url]) ?? []),
        [batchUrls],
    );

    if (images.length === 0) return null;

    return (
        <div className="flex gap-2 flex-wrap mt-1">
            {images.map((image) => (
                <TaskImageThumb
                    key={image.id}
                    image={image}
                    url={urlMap.get(image.id)}
                    onDelete={() => onDeleteImage(image.id)}
                />
            ))}
        </div>
    );
}
